import type { Ref } from 'vue'
import { onUnmounted, onMounted, ref } from 'vue'
import type { PLATFORM_TYPE, CLIENT_TYPE } from '@lib/constants/cdp.const'
import { CDP_EVENT_TYPE } from '@lib/constants/cdp.const'
import { type usePitcherApi, PitcherEventName } from '@sdk/main'
import { isPitcherWkWebView } from '@lib/util/userAgents'
import dayjs from 'dayjs'
import { msToSeconds } from '@lib/util/date'

// Event Types:
// `active` - exit/enter analytics event is called each time user switch browser tab/window
// `passive` - only inactive window time duration is calculated each time user switch browser tab/window
type EventType = 'active' | 'passive'
type EventLifecycle = 'enter' | 'exit'

export type InactiveEventPayload = {
  platform?: (typeof PLATFORM_TYPE)[keyof typeof PLATFORM_TYPE]
  client?: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
  pair_id?: string
  file_id?: string
  [key: string]: any
}

interface InactiveEvent {
  id: string
  pairId: string
  payload: InactiveEventPayload | ((type: EventLifecycle) => InactiveEventPayload)
  enterEvent: (typeof CDP_EVENT_TYPE)[keyof typeof CDP_EVENT_TYPE]
  exitEvent: (typeof CDP_EVENT_TYPE)[keyof typeof CDP_EVENT_TYPE]
  type: EventType
  viewStart: number
  inactiveTime: number
}

const fileDuration = new Map<string, number>()

const PITCHER_API_NOT_INIT = 'Pitcher API is not initialized on iOS app'

const extractPayload = (
  payload: InactiveEventPayload | ((type: EventLifecycle) => InactiveEventPayload),
  type: EventLifecycle,
  pairId: string,
) => {
  const extractedPayload = typeof payload === 'function' ? payload(type) : payload
  return {
    ...extractedPayload,
    pair_id: pairId,
  }
}

const MIN_DURATION_TO_REOPEN_EVENT_PAIR = 150 // 150ms to avoid invalid events due to foreground handlers
// Add a map to track pending focus timeouts
const pendingFocusTimeouts = new Map<string, { timeoutId: number; enterEvent: string; pairId: string }>()

export default function (
  track: (eventName: string, payload: any) => void,
  pitcherApi?: ReturnType<typeof usePitcherApi>,
) {
  const inactiveWindowDuration = ref(0)
  const inactiveStart = ref(Date.now())
  const inactiveEvents: Ref<InactiveEvent[]> = ref([])

  const registerInactiveEvent = ({
    id,
    payload,
    enterEvent,
    exitEvent,
    type = 'active',
  }: {
    id: string
    payload: InactiveEventPayload | ((type?: EventLifecycle) => InactiveEventPayload)
    enterEvent: (typeof CDP_EVENT_TYPE)[keyof typeof CDP_EVENT_TYPE]
    exitEvent: (typeof CDP_EVENT_TYPE)[keyof typeof CDP_EVENT_TYPE]
    type?: EventType
  }): string | void => {
    const eventIndex = inactiveEvents.value.findIndex((event) => event.id === id)
    const viewStart = Date.now()
    const pairId = `${id}_${viewStart}`

    if (eventIndex === -1) {
      inactiveEvents.value.push({
        id,
        pairId,
        inactiveTime: 0,
        viewStart,
        enterEvent,
        exitEvent,
        type,
        payload,
      })

      const trackPayload: Record<string, any> = {
        local_timestamp: dayjs(viewStart).format(),
        ...extractPayload(payload, 'enter', pairId),
      }
      // if event is canvasEntered, add canvas_correlation_id to payload
      if (enterEvent === CDP_EVENT_TYPE.canvasEntered) {
        trackPayload.canvas_correlation_id = pairId
      }

      if (enterEvent === CDP_EVENT_TYPE.fileEntered) {
        fileDuration.set(id, 0)
      }

      track(enterEvent, trackPayload)

      return pairId
    }
  }

  const unregisterInactiveEvent = (id: string, canvas_correlation_id?: string) => {
    const eventIndex = inactiveEvents.value.findIndex((event) => event.id === id)

    // Check if this is a rapid unregister after a focus event
    if (pendingFocusTimeouts.has(id)) {
      const { timeoutId, enterEvent, pairId } = pendingFocusTimeouts.get(id)!
      clearTimeout(timeoutId)
      console.info(`[useWindowEvents] skipping invalid focus/unregister pair for ${enterEvent}: ${id}, pair: ${pairId}`)
      pendingFocusTimeouts.delete(id)

      // Remove the event from the tracking array without logging exit
      if (eventIndex > -1) {
        inactiveEvents.value.splice(eventIndex, 1)
      }

      return // Skip tracking the exit event
    }

    if (eventIndex > -1) {
      const inactiveEvent = inactiveEvents.value.splice(eventIndex, 1)[0]
      const { payload, viewStart, inactiveTime, exitEvent, pairId } = inactiveEvent
      const viewEnd = Date.now()
      let duration = msToSeconds(viewEnd - viewStart - inactiveTime)
      const extractedPayload = extractPayload(payload, 'exit', pairId)
      const fileId = extractedPayload?.file_id

      if (exitEvent === CDP_EVENT_TYPE.pageExited && fileId && fileDuration.has(fileId)) {
        const currentDuration = fileDuration.get(fileId) ?? 0
        fileDuration.set(fileId, duration + currentDuration)
      }

      if (exitEvent === CDP_EVENT_TYPE.fileExited && fileDuration.has(id) && (fileDuration.get(id) ?? 0) > 0) {
        duration = fileDuration.get(id) ?? 0
        fileDuration.delete(id)
      }

      track(exitEvent, {
        local_timestamp: dayjs(viewEnd).format(),
        ...extractedPayload,
        ...(canvas_correlation_id ? { canvas_correlation_id } : {}),
        duration,
      })
    }
  }

  const onWindowFocus = () => {
    inactiveWindowDuration.value = Date.now() - inactiveStart.value

    inactiveEvents.value.forEach((event) => {
      const { payload, enterEvent, pairId } = event

      if (event.type === 'active') {
        // Store the event in a pending state with timeout ID
        const timeoutId = setTimeout(() => {
          if (pendingFocusTimeouts.has(event.id)) {
            event.viewStart = Date.now()
            pendingFocusTimeouts.delete(event.id)

            // console.info('[useWindowEvents] tracking enter on focus: ', event.id, enterEvent, 'pair:', pairId)
            track(enterEvent, {
              local_timestamp: dayjs(event.viewStart).format(),
              ...extractPayload(payload, 'enter', pairId),
            })
          }
        }, MIN_DURATION_TO_REOPEN_EVENT_PAIR) as unknown as number

        pendingFocusTimeouts.set(event.id, { timeoutId, enterEvent, pairId })
      } else if (event.type === 'passive') {
        event.inactiveTime += inactiveWindowDuration.value
      }
    })
  }

  const onWindowBlur = () => {
    inactiveStart.value = Date.now()

    inactiveEvents.value.forEach((event) => {
      const { payload, viewStart, inactiveTime, exitEvent, pairId } = event

      const viewEnd = Date.now()
      const duration = msToSeconds(viewEnd - viewStart - inactiveTime)

      if (event.type === 'active') {
        track(exitEvent, {
          local_timestamp: dayjs(viewEnd).format(),
          ...extractPayload(payload, 'exit', pairId),
          duration,
        })
      }
    })
  }

  const onVisibilityChange = (e: Event) => {
    e.stopPropagation()

    if (document.hidden) {
      onWindowBlur()
    } else {
      onWindowFocus()
    }
  }

  onMounted(() => {
    if (isPitcherWkWebView) {
      if (!pitcherApi) {
        throw new Error(PITCHER_API_NOT_INIT)
      }

      pitcherApi?.on(PitcherEventName.APP_FOREGROUNDED, onWindowFocus)
      pitcherApi?.on(PitcherEventName.APP_BACKGROUNDED, onWindowBlur)
    } else {
      window.addEventListener('visibilitychange', onVisibilityChange)
    }
  })

  onUnmounted(() => {
    if (isPitcherWkWebView) {
      if (!pitcherApi) {
        throw new Error(PITCHER_API_NOT_INIT)
      }

      pitcherApi?.off(PitcherEventName.APP_FOREGROUNDED, onWindowFocus)
      pitcherApi?.off(PitcherEventName.APP_BACKGROUNDED, onWindowBlur)
    } else {
      window.removeEventListener('visibilitychange', onVisibilityChange)
    }
  })

  return { registerInactiveEvent, unregisterInactiveEvent }
}
